import type { ComponentType, ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertIcon, LoadingState, PlugIcon, RefreshIcon } from "@signet/ui";
import { ago, api, UsageCard, Updated } from "@signet/web-kit";

// The card frame and its small parts live in the kit, shared with modules
// (Research draws HikerAPI's card with them).
export { Meter, ProviderName, toneFor, Updated, useNow } from "@signet/web-kit";

/* ──────────────────────────────────────────────────────────────────────────
   The pieces every live provider on the API/AI Usage page is built from.

   A live provider is one GET /api/usage/<id> answering { configured, ok,
   error, fetchedAt, ...its own figures }. The server holds the credentials,
   calls the provider and caches the answer, so the browser never talks to
   Anthropic, Railway or anyone else directly. A new provider is a card file
   using ProviderCard and one entry in LIVE_CARDS (index.tsx).
   ────────────────────────────────────────────────────────────────────────── */

export type LiveResult = { provider: string; configured: boolean; ok: boolean; error?: string; fetchedAt: string; cached?: boolean };

// The last answer that worked, per provider, for as long as the app is open.
// A failed refresh is answered 200 with ok:false and replaces the query's
// data, so without this a provider hiccup would blank figures that were
// right two minutes ago. Shown under the error, with its own time.
const lastGood = new Map<string, LiveResult>();

export function useLiveUsage<T extends LiveResult>(id: string, refetchMs: number) {
  const client = useQueryClient();
  const key = ["usage-live", id];
  const query = useQuery({ queryKey: key, queryFn: () => api<T>(`/api/usage/${id}`), refetchInterval: refetchMs });
  const refresh = useMutation({
    mutationFn: () => api<T>(`/api/usage/${id}?fresh=1`),
    onSuccess: (data) => client.setQueryData(key, data),
  });
  const data = query.data;
  if (data?.ok) lastGood.set(id, data);
  const shown = (data?.ok ? data : lastGood.get(id)) as T | undefined;
  const failure = refresh.error ?? query.error;
  const problem = failure ? (failure as Error).message : data && data.configured && !data.ok ? data.error || "The provider did not answer." : null;
  return {
    loading: query.isLoading,
    notConfigured: data?.configured === false,
    shown,
    problem,
    refresh: () => refresh.mutate(),
    refreshing: refresh.isPending || query.isFetching,
  };
}

/** "Resets in 2 hours, 2:30 PM", or the weekday when it is not today. In the browser's own locale and zone. */
export function resetsLabel(iso: string | null | undefined, now = Date.now()): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const within = at.getTime() - now;
  if (within <= 0) return "Resets any moment";
  const clock = within < 20 * 3_600_000
    ? at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    : at.toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" });
  return `Resets ${ago(iso, now)}, ${clock}`;
}

/** Dollars in the browser's locale, always with cents: these are bills, not estimates of scale. */
export function usd(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

/**
 * One live provider's card: its name and mark, a line on what it measures,
 * a refresh button, then whichever of loading, not connected, the figures,
 * or the error over the last figures that worked.
 */
export function ProviderCard<T extends LiveResult>({
  id,
  title,
  logo,
  caption,
  envVar,
  live,
  children,
}: {
  id: string;
  title: string;
  logo?: ComponentType<{ size?: number }>;
  caption: ReactNode;
  /** The server variable that connects it, named in the Not connected state. */
  envVar: string;
  live: ReturnType<typeof useLiveUsage<T>>;
  children: (data: T) => ReactNode;
}) {
  return (
    <UsageCard
      id={id}
      title={title}
      logo={logo}
      caption={caption}
      actions={live.notConfigured ? null : (
        <button type="button" className="icon-button small ghost" onClick={live.refresh} disabled={live.refreshing} aria-label={`Refresh ${title}`} title="Refresh now">
          <RefreshIcon size={13} />
        </button>
      )}
      foot={live.shown && !live.notConfigured ? <Updated at={live.shown.fetchedAt} /> : null}
    >
      {live.loading ? (
        <LoadingState />
      ) : live.notConfigured ? (
        <div className="signet-usage-off">
          <PlugIcon size={15} />
          <span><strong>Not connected.</strong> Set <code>{envVar}</code> on the server to show it here.</span>
        </div>
      ) : (
        <>
          {live.problem ? (
            <p className="signet-usage-problem" role="alert">
              <AlertIcon size={13} />
              <span>{live.problem}{live.shown ? " Showing the last figures that worked." : ""}</span>
            </p>
          ) : null}
          {live.shown ? children(live.shown) : null}
        </>
      )}
    </UsageCard>
  );
}
